import { useCallback, useEffect, useRef } from 'react';
import { overlay } from 'overlay-kit';
import { useUser } from '@/lib/auth';
import { CookieManager } from '@/shared/cookies';
import { markPromotionModalShown, wasPromotionModalShown } from '@/shared/features/promotion-modal-state';
import { PointReleaseModal } from '../components/PointReleaseModal';

/**
 * 클래스 출시 모달과 다른 키. 계정당 1회(영구).
 */
const POINT_RELEASE_COOKIE = 'point_release_shown';

/**
 * 포인트 출시 안내 모달 게이트.
 *
 * - **계정당 1회.** 앱 진입 시 선생님(학생 외)에게 자동으로 딱 한 번.
 * - 로드형 모달이라 세션 게이트(`wasPromotionModalShown`)에 묶는다 —
 *   클래스 출시·휴대폰 인증 등 다른 자동 모달과 한 세션에 겹쳐 뜨지 않게 한다.
 *   (설명 모달은 온클릭이라 세션 게이트를 쓰지 않는 것과 대비)
 */
export const usePointReleaseModal = (triggeredOn = 'point_release_auto_promotion') => {
  const {
    isInitialized,
    user
  } = useUser();
  const canShowToAudience = !!user && user.type?.toLowerCase() !== 'student';
  const hasOpenedRef = useRef(false);
  const isDismissed = useCallback(() => new CookieManager(POINT_RELEASE_COOKIE).has(), []);
  const dismiss = useCallback(() => {
    new CookieManager(POINT_RELEASE_COOKIE, {
      expires: new Date('2099-12-31')
    }).set('true');
  }, []);
  const openModal = useCallback(() => {
    overlay.open(({
      close,
      isOpen
    }) => <PointReleaseModal isOpen={isOpen} triggeredOn={triggeredOn} onClose={() => {
      dismiss();
      close();
    }} />);
  }, [dismiss, triggeredOn]);
  const shouldShowModal = useCallback(() => {
    if (!isInitialized) return false;
    if (!canShowToAudience) return false;
    if (isDismissed()) return false;
    if (wasPromotionModalShown()) return false;
    return true;
  }, [isInitialized, canShowToAudience, isDismissed]);
  useEffect(() => {
    if (hasOpenedRef.current) return;
    if (shouldShowModal()) {
      hasOpenedRef.current = true;
      markPromotionModalShown();
      dismiss();
      openModal();
    }
  }, [shouldShowModal, openModal, dismiss]);
  return {
    openModal,
    dismiss,
    isDismissed
  };
};